import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

type StyleProps = {
  hasAnyQualify?: boolean | null;
}

export const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) =>
  createStyles({
    root: {
      height: '100%',
      position: 'relative',
    },
    scrollContainer: {
      height: '100%',
      flexGrow: 1,
      overflowY: 'auto'
    },
    simpleBar: {
      height: '100%',
    },
    progress: {
      position: 'absolute',
      right: '50%',
      bottom: '50%',

      '&.data-loaded': {
        position: 'absolute',
        right: '7px',
        bottom: '7px',
      }
    },
    victoryCeremonyInfo: {
      background: theme.palette.records.lighter,
      textTransform: 'uppercase',
      fontSize: '12px',
      letterSpacing: '1px',
      color: '#fff',
      padding: '6px 8px 0px 13px',

      '& span': {
        fontWeight: 'bold',
      }
    },
    startListTable: {
      borderCollapse: 'collapse',
      width: '100%',
      fontWeight: 'bold',

      '& td, th': {
        whiteSpace: 'nowrap',
      },
      '& thead th': {
        border: `0px solid ${theme.palette.table.borderColor}`,
        background: theme.palette.records.lighter,
        borderTop: '0',
        borderLeft: '0',
        textTransform: 'uppercase',
        fontSize: '12px',
        letterSpacing: '1px',
        color: theme.palette.primary.light,
        padding: '5px 8px 5px 6px',
        position: 'sticky',
        zIndex: '2',
        top: '0',

        '&.has-multiple-phases': {
          paddingTop: '3px',
        },
        '& .bib': {
          float: 'right',
          opacity: '0.8',
        },
        '& .athlete-count': {
          paddingLeft: '8px',
          color: '#fff',
          fontWeight: 'normal',
          opacity: '0.8',
          paddingRight: '8px',
        }
      },
      '& thead th:last-child': {
        borderRight: '0',
      },
      '& tbody tr:nth-child(even)': {
        background: theme.palette.table.altRowBackground,

        '& .col-pb, .col-sb, .col-result, .col-points, .col-gap, .col-reaction, .col-record': {
          background: theme.palette.table.altColBackground,
        }
      },
      '& tbody tr:nth-child(odd)': {
        '& .col-pb, .col-sb, .col-result, .col-points, .col-gap, .col-reaction, .col-record': {
          background: theme.palette.table.altOddColBackground,
        }
      },
      '& tbody tr td': {
        borderBottom: `1px solid ${theme.palette.table.borderColor}`,
        borderTop: '0',
        borderLeft: '0',
        padding: '3px 8px',
      },
      '& tbody tr td:last-child': {
        borderRight: '0',
      },
      '& td.col-lane': {
        fontSize: '18px',
        verticalAlign: 'top',
        paddingTop: '8px',
      },
      '& td.col-lane-relays': {
        verticalAlign: 'top',
        paddingTop: '12px',
      },
      '& td.col-athlete': {
        position: 'relative',

        '& .athlete-flag-container': {
          display: 'flex',
        },
        '& .flag-container': {
          position: 'relative',
          width: '41px',
          height: '27px',
          marginRight: '15px',
          marginTop: '5px',
          border: `1px solid ${theme.palette.table.borderColor}`,
        },
        '& .flag': {
          width: '41px',
          height: '27px',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '41px 27px',
          position: 'absolute',
          top: 0,
          left: '-1px',
        },
        '& .athlete-container.has-bibs': {
          paddingRight: '45px',
        },
        '& .athlete-name': {
          fontSize: '16px',

          '& .first-name': {
            fontWeight: 'normal',
            opacity: '0.8',
          },
          '& .relay-name': {
            fontSize: '19px',
            fontWeight: 'bold',
            opacity: '1',
          },
          '& .relay-label': {
            paddingLeft: '7px',
            opacity: '1',
          },
          '& .relay-members': {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            listStyleType: 'none',
            fontWeight: 'normal',
            fontSize: '14px',
            margin: '0',
            padding: '0',

            '& li': {
              padding: '2px 5px 2px 0',
              fontSize: '12px',
              width: '220px',
            },
            '& .member-yob': {
              opacity: '0.5',
              paddingLeft: '4px',
            },
            '& .member-last-name': {
              fontWeight: 'bold',
              fontSize: '13px',
              paddingLeft: '3px',
            },
            '& .member-first-name': {
              opacity: '0.8',
            },
            '& .member-order': {
              background: theme.palette.secondary.dark,
              borderRadius: '4px',
              padding: '2px 4px',
              fontWeight: 'bold',
            }
          }
        },
        '& .club-name': {
          fontWeight: 'normal',
          color: theme.palette.textTabs.club,
          fontSize: '13px',
        },
        '& .athlete-coach': {
          fontWeight: 'bold',
          marginTop: '8px',
          fontSize: '13px',

          '& span': {
            fontWeight: 'normal',
          }
        },
        '& .startlist-note': {
          fontSize: '12px',
          display: 'inline-block',
          marginLeft: '5px',
          fontWeight: 'normal',
          background: theme.palette.primary.main,
          borderRadius: '4px',
          padding: '1px 6px 0',
          opacity: '0.9',
        },
        '& .bib': {
          fontSize: '17px',
          color: theme.palette.textTabs.bib,
          position: 'absolute',
          right: '0',
          top: '9px',
        }
      },
      '& td.col-yob': {
        fontWeight: 'normal',
        '& span': {
          display: 'block',
          fontSize: '11px',
          opacity: '0.5'
        }
      },
      '& td.col-result': {
        position: 'relative',
        paddingLeft: '15px',
        borderLeft: `1px solid ${theme.palette.table.borderColor}`,
        borderRight: `1px solid ${theme.palette.table.borderColor}`,
        paddingRight: ({ hasAnyQualify }) => hasAnyQualify ? '22px' : '8px',

        '& .result-record-container': {
          display: 'flex',
          flexDirection: 'row-reverse',
        },
        '& .result-disqualify-rule': {
          fontWeight: 'normal',
          fontSize: '13px',
          lineHeight: '24px',
          paddingRight: '6px',
          opacity: '0.7',
        },
        '& .result': {
          fontSize: '16px',
        },
        '& .result-qualify': {
          fontSize: '14px',
          color: theme.palette.primary.light,
          fontWeight: 'normal',
          paddingLeft: '0',
          position: 'absolute',
          top: '13px',
          right: '6px',

          '&.conflicting': {
            fontSize: '12px',
            top: '15px',
            right: '4px',
          }
        }
      },
      '& td.col-gap': {
        borderRight: `1px solid ${theme.palette.table.borderColor}`,
        fontWeight: 'normal',
        opacity: '0.7',
      },
      '& td.col-points': {
        borderRight: `1px solid ${theme.palette.table.borderColor}`,
        lineHeight: '13px',
      },
      '& td.col-reaction': {
        borderRight: `1px solid ${theme.palette.table.borderColor}`,
        fontWeight: 'normal',
        paddingTop: '4px',
        fontSize: '14px',
        position: 'relative',
        paddingRight: '34px',
        opacity: '0.7',

        '& .fastest::before': {
          content: '"\\2022"',
          color: theme.palette.primary.light,
          opacity: '1',
          fontWeight: 'bold',
          paddingRight: '4px',
          fontSize: '19px',
          lineHeight: '10px'
        },
        '& .reaction-rank': {
          opacity: '0.5',
          fontSize: '12px',
          position: 'absolute',
          top: 'calc(50% - 8px)',
          right: '8px',
        },
        '& .reaction-rank::before': {
          content: '"("',
          paddingRight: '1px',
          opacity: '0.4',
        },
        '& .reaction-rank::after': {
          content: '")"',
          paddingLeft: '1px',
          opacity: '0.4',
        }
      },
      '& td.col-result-lane': {
        fontWeight: 'normal',
        fontSize: '13px',
        opacity: '0.7',
        paddingLeft: '25px',
      },
      '& td.col-result-pb-sb': {
        borderLeft: 'none',
        opacity: '0.7',
        fontWeight: 'normal',
        fontSize: '13px',

        '& .result-sb': {
          fontSize: '11px',
        }
      },
      '& td.col-pb': {
        borderLeft: `1px solid ${theme.palette.table.borderColor}`,
      },
      '& td.col-sb': {
        borderLeft: `1px solid ${theme.palette.table.borderColor}`,
      },
      '& td.col-warank': {
        borderLeft: `1px solid ${theme.palette.table.borderColor}`,
        borderRight: `1px solid ${theme.palette.table.borderColor}`,
        fontWeight: 'normal',
      },
      '& td.col-qualify': {
        borderLeft: `1px solid ${theme.palette.table.borderColor}`,
        fontWeight: 'normal',

        '& .qualify-type': {
          color: theme.palette.primary.light,
          fontWeight: 'bold',
          display: 'inline-block',
          width: '23px',
          marginRight: '3px',
          fontSize: '13px',
        }
      },
      '& .col-no-athletes': {
        fontSize: '18px',
        textAlign: 'center',
        padding: '30px 10px',

        '& div': {
          /* Docházelo k problikávání "No athletes in startlist", takto obejito fade efektem */
          animationName: 'fadeIn',
          animationDuration: '1s',
          transitionTimingFunction: 'linear',
        }
      },
      '& .text-right': {
        textAlign: 'right',
      },
      '& .text-left': {
        textAlign: 'left',
      },
      '& .text-center': {
        textAlign: 'center',
      },

      '&:not(.has-startlist-data)': {
        '& th .bib': {
          marginRight: '8px',
        },
        '& td.col-athlete': {
          paddingRight: '30px',

          '& .bib': {
            right: '8px',
          }
        },
      },
      '& tbody tr': {
        '&.place-first td, &.place-second td, &.place-third td': {
          color: '#000',

          '& .record, .result-qualify': {
            color: '#000 !important',
          },

          '& .bib': {
            color: 'rgba(0,0,0,0.8)',
          },
          '& .club-name': {
            color: 'rgba(0,0,0,0.8)',
          }
        },
        '&.place-first td': {
          backgroundColor: '#FFC540 !important',

          '& .relay-members .member-order': {
            backgroundColor: '#FFC540 !important',
          },
          '&.col-lane': {
            fontSize: '24px',
          },
          '&.col-athlete': {
            '& .athlete-name': {
              fontSize: '24px',
            }
          },
          '&.col-result .result-qualify': {
            top: '20px',
          }
        },
        '&.place-second td': {
          backgroundColor: '#D3D3D3 !important',

          '& .relay-members .member-order': {
            backgroundColor: '#D3D3D3 !important',
          },
          '&.col-lane': {
            fontSize: '20px',
          },
          '&.col-athlete': {
            '& .athlete-name': {
              fontSize: '20px',
            }
          },
          '&.col-result .result-qualify': {
            top: '16px',
          }
        },
        '&.place-third td': {
          backgroundColor: '#DC8B53 !important',

          '& .relay-members .member-order': {
            backgroundColor: '#DC8B53 !important',
          },
        },
      },
    },
  }),
);